import { Api } from '@monorepo/shared/apiClient';
import {
  FormTemplatesCacheEvents,
  invalidateFormTemplatesCache,
} from '@monorepo/shared/cacheRegisters/forms/formTemplatesCacheRegister';
import { useToast } from '@monorepo/shared/contexts';
import { useCallback } from 'react';
import { useMutation } from 'react-query';
import { EditableFormTemplate } from '../../FormTemplateEditor/types';

const THIRTY_SECONDS = 30 * 1000;

interface UseFormTemplateCreateProps {
  navigateOnSuccess: (props: { formTemplateSlug: string }) => void;
  successToastMessage: string;
  organizationId?: string;
  prepForNavigationOnSuccess: () => void;
}

export const useFormTemplateCreate = ({
  navigateOnSuccess,
  successToastMessage,
  organizationId,
  prepForNavigationOnSuccess,
}: UseFormTemplateCreateProps) => {
  const { showUserFriendlyErrorToast, success: showSuccessToast } = useToast();

  const [createFormTemplate, createFormTemplateState] = useMutation(
    Api.createFormTemplate,
    {
      onSuccess: (savedFormTemplate) => {
        // a workaround - practically marking the template as not dirty early,
        // so React state updates fully and doesn't prevent navigating away
        prepForNavigationOnSuccess();
        showSuccessToast(successToastMessage, {
          autoHideDuration: THIRTY_SECONDS,
        });
        invalidateFormTemplatesCache(FormTemplatesCacheEvents.CREATE, {});
        navigateOnSuccess({ formTemplateSlug: savedFormTemplate.slug });
      },
      onError: (error) => {
        showUserFriendlyErrorToast(error, `Unable to save form.`, {
          dontAutoHide: true,
        });
      },
    },
  );

  const createFormTemplateWithBakedInOrg = useCallback(
    async (formTemplate: EditableFormTemplate) =>
      createFormTemplate({ formTemplate, organizationId }),
    [createFormTemplate, organizationId],
  );

  return [createFormTemplateWithBakedInOrg, createFormTemplateState] as const;
};
